import { motion } from "framer-motion";
import { useState } from "react";
import { FaClock, FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import contactBg from "../assets/contact.webp";

function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);

    try {
      const res = await fetch("/send-email.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, formType: "contact" }),
      });

      const data = await res.json();

      if (data.ok) {
        setStatus("ok");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        setStatus("error");
        console.error(data.error);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setStatus("error");
    } finally {
      setSending(false);
    }
  };


  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" }
    }),
  };

  return (
    <div className="font-sans w-full">

      <section
        className="relative w-full h-[40vh] bg-cover bg-center flex flex-col justify-center items-center"
        style={{ backgroundImage: `url(${contactBg})` }}
      >

        <div className="absolute inset-0 bg-black bg-opacity-60"></div>


        <motion.div
          className="relative z-10 text-center text-white px-4 max-w-3xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="text-white text-[32px] sm:text-[48px] 
                     font-extrabold mb-6 leading-tight">
            Contact Us
          </h1>
          <p className="text-white text-base 
                     mb-8 sm:mb-10 max-w-[90%] sm:max-w-2xl">
            We’d love to hear from you! Whether you have a question, feedback,
            or simply want to connect.
          </p>
        </motion.div>
      </section>


      <section className="py-12 text-center px-6 max-w-6xl mx-auto">
        <motion.p
          className="text-orange-500 italic mb-2"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          Contact Us
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 leading-snug"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Get In Touch With Altair
        </motion.h2>
        <motion.p
          className="mt-4 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          For inquiries,  requests, or further information about our services,
          please contact us using the details provided below
        </motion.p>


        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: <FaMapMarkerAlt />, title: "Our Location", text: "Simba Oil, Goba, Dar es Salaam, Tanzania", dark: true },
            { icon: <FaPhoneAlt />, title: "Phone Number", text: "255714183424", dark: false },
            { icon: <FaEnvelope />, title: "Email Us", text: "info@altair.co.tz", dark: true },
            { icon: <FaClock />, title: "Working Hours", text: "Mon - Sat: 8.00 - 5.00", dark: false }
          ].map((card, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ scale: 1.05 }}
              className={`${card.dark ? "bg-gray-950 text-white" : "bg-white text-gray-700"} rounded-lg p-6 shadow cursor-pointer`}
            >
              <div className="text-2xl text-orange-500 mx-auto mb-3">
                {card.icon}
              </div>
              <h3 className="font-semibold">{card.title}</h3>
              <p className="text-sm mt-2">{card.text}</p>
            </motion.div>
          ))}
        </div>

      </section>


      <section className="py-12 px-6 mb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 py-6">

          <motion.div
            className="rounded-lg overflow-hidden shadow"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* <iframe
              title="map"
             src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127255.3389654305!2d39.14356039999999!3d-6.7795634!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c5b5a5d35e6c5%3A0x525e911f7b9a3665!2sSimba%20Oil%2C%20Goba%2C%20Ubungo%2C%20Dar%20es%20Salaam%2C%2016103%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1610000000000!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            ></iframe> */}
            <iframe
              title="Simba Oil Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127255.3389654305!2d39.1435604!3d-6.7795634!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c4d9c3c72b9b7%3A0x6c0c4d97b72d6a0d!2sSimba%20Oil%2C%20Goba%2C%20Ubungo%2C%20Dar%20es%20Salaam%2C%2016103%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1725634022658!5m2!1sen!2sus"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowfullscreen=""
              loading="lazy">
            </iframe>


          </motion.div>


          <motion.div
            className="bg-white rounded-lg shadow px-6"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your Name"
                className="w-full border px-4 py-2 rounded-lg"
                required
              />
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Your Email"
                type="email"
                className="w-full border px-4 py-2 rounded-lg"
                required
              />
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Your Phone Number"
                type="tel"
                className="w-full border px-4 py-2 rounded-lg"
                required
              />
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Subject"
                className="w-full border px-4 py-2 rounded-lg"
              />
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Message"
                rows="4"
                className="w-full border px-4 py-2 rounded-lg"
                required
              />
              <button
                type="submit"
                disabled={sending}
                className="bg-orange-500 text-white px-6 py-3 rounded-lg"
              >
                {sending ? "SENDING..." : "SEND MESSAGE"}
              </button>
              {status === "ok" && <p className="text-green-600">Message sent!</p>}
              {status === "error" && (
                <p className="text-red-600">Failed to send. Try again.</p>
              )}
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
